import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { Input } from '../Input/Input';
import { Loader } from '../Loader/Loader';
import { FittedTags } from './FittedTags';
import type { FieldAppearance } from '../fieldAppearance';
import { Fragment, ReactNode } from 'react';

type AutocompleteSharedProps<T> = {
  label?: string;
  /**
   * `default` — grey field + label above.
   * `plain` — white field + label above (same layout as default).
   * @default 'default'
   */
  appearance?: FieldAppearance;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  options: T[];
  /** Icon inside the field before the text (e.g. search). Layout is handled by `Input`. */
  startIcon?: ReactNode;
  /** Extra icon before the dropdown/clear indicators (e.g. loading spinner). */
  endAdornment?: ReactNode;
};

type MuiAutocompletePassthroughProps<
  T,
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined = false
> = Pick<
  MuiAutocompleteProps<T, Multiple extends true ? true : false, false, FreeSolo extends true ? true : false, React.ElementType>,
  | 'onChange'
  | 'blurOnSelect'
  | 'isOptionEqualToValue'
  | 'getOptionLabel'
  | 'disableCloseOnSelect'
  | 'autoComplete'
  | 'inputValue'
  | 'onInputChange'
  | 'loading'
  | 'noOptionsText'
  | 'loadingText'
  | 'renderTags'
  | 'limitTags'
  | 'getLimitTagsText'
  | 'renderOption'
  | 'clearText'
  | 'openText'
  | 'closeText'
  | 'groupBy'
  | 'filterOptions'
  | 'clearOnEscape'
  | 'clearOnBlur'
>;

export type AutocompleteProps<T> =
  | (AutocompleteSharedProps<T> & {
      multiple?: false;
      freeSolo?: false;
      value: T | null;
    } & MuiAutocompletePassthroughProps<T, false, false>)
  | (AutocompleteSharedProps<T> & {
      multiple?: false;
      freeSolo: true;
      value: T | null;
    } & MuiAutocompletePassthroughProps<T, false, true>)
  | (AutocompleteSharedProps<T> & {
      multiple: true;
      value: T[];
    } & MuiAutocompletePassthroughProps<T, true, false>);

type ResolvedAutocompleteProps<T> = AutocompleteProps<T> &
  AutocompleteSharedProps<T> & {
    multiple?: boolean;
    freeSolo?: boolean;
  };

function renderAutocompleteInput<T>({
  label,
  appearance = 'default',
  placeholder,
  fullWidth = true,
  disabled = false,
  error = false,
  helperText,
  startIcon,
  endAdornment,
  loading
}: AutocompleteSharedProps<T> & { loading?: boolean }) {
  // Reserve the loader's slot whenever the caller opted into `loading` at all (even while
  // currently `false`), so the field's width/padding never jumps when loading toggles on/off.
  const endIcon =
    loading !== undefined ? (
      <Fragment>
        <span
          className={
            loading ? 'SmInputAdornment-loaderSlot' : 'SmInputAdornment-loaderSlot SmInputAdornment-loaderSlot-hidden'
          }
        >
          <Loader size={16} />
        </span>
        {endAdornment}
      </Fragment>
    ) : (
      endAdornment
    );

  return function AutocompleteInput(params: Parameters<
    NonNullable<MuiAutocompleteProps<T, boolean, false, false, React.ElementType>['renderInput']>
  >[0]) {
    const { ref: rootRef, startAdornment, endAdornment: muiEndAdornment } = params.InputProps;
    const { ref: nativeInputRef, ...nativeInputProps } = params.inputProps;

    return (
      <Input
        label={label}
        appearance={appearance}
        placeholder={placeholder}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        helperText={helperText}
        ref={rootRef as React.Ref<HTMLDivElement>}
        startIcon={startIcon}
        inputStartAdornment={startAdornment}
        endIcon={endIcon}
        inputEndAdornment={muiEndAdornment}
        inputRef={nativeInputRef as React.Ref<HTMLInputElement>}
        inputProps={nativeInputProps}
      />
    );
  };
}

/** Same fallback MUI uses when `getOptionLabel` is not provided. */
function defaultOptionLabel<T>(option: T): string {
  return (option as { label?: string }).label ?? String(option);
}

function autocompleteClassName(appearance: FieldAppearance = 'default', multiple = false) {
  const isPlain = appearance === 'plain';
  return [
    'SmAutocomplete-root',
    isPlain ? 'SmAutocomplete-appearance-plain' : null,
    multiple ? 'SmAutocomplete-multiple' : null
  ]
    .filter(Boolean)
    .join(' ');
}

/** Autocomplete that reuses the design-system `Input` for identical field chrome. */
export function Autocomplete<T>(
  props: AutocompleteSharedProps<T> & { multiple: true; value: T[] } & MuiAutocompletePassthroughProps<T, true, false>
): JSX.Element;
export function Autocomplete<T>(
  props: AutocompleteSharedProps<T> & { multiple?: false; freeSolo: true; value: T | null } & MuiAutocompletePassthroughProps<T, false, true>
): JSX.Element;
export function Autocomplete<T>(
  props: AutocompleteSharedProps<T> & { multiple?: false; freeSolo?: false; value: T | null } & MuiAutocompletePassthroughProps<T, false, false>
): JSX.Element;
export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const {
    label,
    appearance = 'default',
    placeholder,
    fullWidth = true,
    disabled = false,
    error = false,
    helperText,
    options,
    startIcon,
    endAdornment,
    multiple,
    value,
    onChange,
    getOptionLabel,
    isOptionEqualToValue,
    blurOnSelect: blurOnSelectProp,
    disableCloseOnSelect = false,
    autoComplete = true,
    onInputChange,
    loading,
    noOptionsText,
    loadingText,
    renderTags,
    limitTags,
    getLimitTagsText,
    renderOption,
    clearText,
    openText,
    closeText,
    freeSolo = false,
    groupBy,
    filterOptions,
    clearOnBlur,
    clearOnEscape,
    inputValue
  } = props as ResolvedAutocompleteProps<T>;

  const blurOnSelect = blurOnSelectProp ?? (multiple && disableCloseOnSelect ? false : true);

  const sharedProps = {
    className: autocompleteClassName(appearance, multiple),
    fullWidth,
    disabled,
    onInputChange,
    blurOnSelect,
    options,
    getOptionLabel,
    isOptionEqualToValue,
    popupIcon: <KeyboardArrowDownIcon />,
    disableCloseOnSelect,
    autoComplete,
    loading,
    loadingText,
    noOptionsText,
    clearText,
    openText,
    closeText,
    groupBy,
    filterOptions,
    clearOnBlur,
    clearOnEscape,
    inputValue,
    renderInput: renderAutocompleteInput({
      label,
      appearance,
      placeholder,
      fullWidth,
      disabled,
      error,
      helperText,
      options,
      startIcon,
      endAdornment,
      loading
    }),
    slotProps: {
      paper: { className: 'SmSelectMenu-paper' },
      clearIndicator: { disableRipple: true, size: 'small' as const },
      popupIndicator: { disableRipple: true, size: 'small' as const }
    },
    ListboxProps: { className: 'SmSelectMenu-list' },
  };

  if (multiple) {
    return (
      <MuiAutocomplete<T, true, false, false>
        {...sharedProps}
        multiple
        value={value as T[]}
        onChange={onChange as MuiAutocompleteProps<T, true, false, false>['onChange']}
        limitTags={limitTags}
        getLimitTagsText={getLimitTagsText}
        renderTags={
          (renderTags as MuiAutocompleteProps<T, true, false, false>['renderTags']) ??
          ((tags, getTagProps) => (
            <FittedTags value={tags} getTagProps={getTagProps} getLabel={getOptionLabel ?? defaultOptionLabel} />
          ))
        }
        renderOption={renderOption as MuiAutocompleteProps<T, true, false, false>['renderOption']}
      />
    );
  }

  if (freeSolo) {
    return (
      <MuiAutocomplete<T, false, false, true>
        {...(sharedProps as MuiAutocompleteProps<T, false, false, true>)}
        freeSolo
        value={value as T | null}
        onChange={onChange as MuiAutocompleteProps<T, false, false, true>['onChange']}
        renderOption={renderOption as MuiAutocompleteProps<T, false, false, true>['renderOption']}
      />
    );
  }

  return (
    <MuiAutocomplete<T, false, false, false>
      {...sharedProps}
      value={value as T | null}
      onChange={onChange as MuiAutocompleteProps<T, false, false, false>['onChange']}
      renderOption={renderOption as MuiAutocompleteProps<T, false, false, false>['renderOption']}
    />
  );
}
